import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import { isUkPostcode } from '@sainsburys-tech/bolt-validators'
import CollectionBox from './CollectionBox/CollectionBox'
import DeliveryBox from './DeliveryBox/DeliveryBox'
import CheckStoreBox from './CheckStoreBox/CheckStoreBox'
import updateDigitalData, { doTrack, pushEventAndTrack } from '../../helpers/taggingSerializer'
import setFulfilmentTabCookie from '../../helpers/setFulfilmentTabCookie'
import { useBrandConfigContext } from '../../providers/BrandConfigContext/BrandConfigContext'
import { FulfilmentBoxV2 } from '@/components/FulfilmentBox/FulfilmentBoxV2/FulfilmentBoxV2'

const StoreSelectorLightBox = loadable(() =>
  import(/* webpackChunkName: "store-selector-light-box" */ './StoreSelectorLightBox/StoreSelectorLightBox')
)

const updateDataObjectAndTrack = (eventName, props, initialMount, brandConfig) => {
  const stores = {
    productStore: props.productStore,
    bazaarVoiceReviewStore: props.bazaarVoiceReviewStore,
    bazaarVoiceQuestionAnswerStore: props.bazaarVoiceQuestionAnswerStore,
    storeLocatorStore: props.storeLocator,
    alternativeProductStore: props.alternativesStore,
    featuredProductsStore: props.featuredProductsStore,
    specialOffersCreatorStore: props.specialOffersCreator
  }

  updateDigitalData(
    stores,
    initialMount ? 'the fulfilment box was localised' : 'the fulfilment box was updated',
    brandConfig
  )
  doTrack(eventName)
}

const getRenderOptions = props => {
  const {
    globallyOutOfStock,
    endOfLineOutOfStock,
    preorderUnavailable,
    inStoreJewelleryItem,
    digitalDownload,
    twoManDeliveryUnavailable
  } = props.attributes
  const hasFeatureType =
    globallyOutOfStock ||
    endOfLineOutOfStock ||
    preorderUnavailable ||
    inStoreJewelleryItem ||
    digitalDownload ||
    twoManDeliveryUnavailable

  const collectable = props.attributes.collectable && !hasFeatureType
  const deliverable = props.attributes.deliverable && !hasFeatureType
  return {
    hasFeatureType,
    collectable,
    deliverable
  }
}

const searchDelivery = async (props, postcode, updateIsInitialMount, userCd = false) => {
  const fulfilmentPayload = props.fulfilmentPayload || {}
  await props.localiseFulfilment({
    ...fulfilmentPayload,
    postcode: postcode.trim(),
    landingPageRequest: !userCd,
    deliveryRequest: true
  })

  if (userCd) {
    updateIsInitialMount('pdp_newHDPostcodeStockAPIs')
  }

  pushEventAndTrack({
    eventInfo: {
      eventName: 'pdpCheckStockHD',
      eventAction: 'pdp check stock home delivery'
    },
    attributes: {
      productId: props.attributes.partNumber
    }
  })

  pushEventAndTrack({
    eventInfo: {
      eventName: 'pdp_deliveryLocationCheck',
      eventAction: 'Check Location For Delivery',
      productID: props.attributes.partNumber // added from openStoreSelector method. Is this expected behaviour
    }
  })
}

const chooseAlternativeStore = async (event, props, updateIsInitialMount) => {
  event.preventDefault()

  const {
    attributes,
    storeLocator: { alternativeStore }
  } = props
  const storeId = parseInt(alternativeStore.storeId, 10)

  const fulfilmentPayload = props.fulfilmentPayload || {}
  await props.localiseFulfilment({
    ...fulfilmentPayload,
    origin: `st:${storeId}`,
    fromStoreSelector: true,
    fromChooseAlternativeStore: true,
    landingPageRequest: false
  })

  updateIsInitialMount('pdp_AlternativeNewStoreStockAPIs')

  pushEventAndTrack({
    eventInfo: {
      eventName: 'pdp_collectionAlternativeStore',
      eventAction: 'Change to alternative store'
    },
    attributes: {
      productID: attributes.partNumber
    }
  })

  pushEventAndTrack({
    eventInfo: {
      eventName: 'pdp_fulfillmentModulesInteraction',
      eventAction: 'PDP Fulfillment Modules Interaction'
    },
    attributes: {
      moduleType: 'collection',
      clickOrigin: 'collectionSelectStore'
    }
  })
}

const addToTrolley = (event, collectionTab, trolleyButtonTracking) => {
  event.preventDefault()

  const isCollection = collectionTab || event.target.parentNode.parentNode.className.includes('collection')

  setFulfilmentTabCookie(isCollection)

  if (isCollection) {
    trolleyButtonTracking('PDP:collectionATT')()
  } else {
    trolleyButtonTracking('PDP:deliveryATT')()
  }
}

const getRegionId = () => {
  const params = new URL(window.location.href).searchParams
  const regionId = params.get('region_id')
  // Checks regionId is alphanumeric
  if (regionId && /^[0-9a-zA-Z]+$/.test(regionId)) {
    return regionId
  }
  return null
}

function FulfilmentBox(props) {
  const [storeSelectorVisible, setStoreSelectorVisible] = useState(false)
  const [initialSearchTerm, setInitialSearchTerm] = useState('')
  const [deliverySearchTerm, setDeliverySearchTerm] = useState('')
  const [storeSelectorSearchTerm, setStoreSelectorSearchTerm] = useState('')
  const [showDeliveryForm, setShowDeliveryForm] = useState(
    props.attributes.deliverable && !props.storeLocator.deliveryMessage
  ) // TODO move to redux store?
  const isInitialMount = useRef('pdp_initialStockAPIs') // use ref to keep track of the component's initial mount - set isInitialMount.current = true in the last effect to avoid unwated behaviours
  const updateIsInitialMount = newValue => {
    isInitialMount.current = newValue
  }
  const brandConfig = useBrandConfigContext()
  const updateDataLayer = () => {
    if (isInitialMount.current === 'pdp_initialStockAPIs') {
      updateDataObjectAndTrack('pdp_initialStockAPIs', props, true, brandConfig)
      updateIsInitialMount('pdp_variantStockAPIs')
    } else {
      updateDataObjectAndTrack(isInitialMount.current, props, false, brandConfig)
      updateIsInitialMount('pdp_variantStockAPIs')
    }
  }

  useEffect(() => {
    const maxNumberOfRetries = 5
    const fetchCISCookie = () => {
      const cookies = global.document.cookie
      const cisIdCookie = cookies.split(';').filter(cookie => cookie.trim().startsWith('cisId='))
      const cookieStatus = cisIdCookie?.length ? cisIdCookie[0].split('=')[1] : false

      props.updateCisCookieStatus(cookieStatus)
    }

    // setInterval is used to account for a delay in the cookie being set when region ID is provided
    if (getRegionId()) {
      let count = 1
      const interval = setInterval(() => {
        fetchCISCookie()

        if (props.storeLocator.cisCookieStatus !== false || count === maxNumberOfRetries) {
          clearInterval(interval)
        }

        count += 1
      }, 250)
    } else {
      fetchCISCookie()
    }
  }, [])

  useEffect(() => {
    const {
      quantity,
      attributes: { partNumber },
      storeLocator: { currentSearchTerm, deliveryPostcode, cisCookieStatus },
      localiseFulfilment
    } = props

    const payload = {
      partNumber,
      quantity,
      deliveryPostcode,
      landingPageRequest: true,
      storedSearchTerm: currentSearchTerm,
      regionId: getRegionId()
    }
    if (cisCookieStatus) {
      localiseFulfilment(payload)
    }
  }, [props.attributes.partNumber, props.storeLocator.cisCookieStatus])

  useEffect(() => {
    const {
      attributes: { deliverable },
      storeLocator: { deliveryMessage }
    } = props
    if (deliverable && deliveryMessage) {
      setShowDeliveryForm(false)
    }
  }, [props.attributes.deliverable, props.storeLocator.deliveryMessage, props.storeLocator.deliveryUpdateTimestamp])

  useEffect(() => {
    async function fetchAlternativeStore() {
      await props.getAlternativeStore({
        skuQty: `${props.attributes.partNumber}_${props.quantity}`,
        nst: props.storeLocator.storeNumber,
        channel: 'web_pdp'
      })
    }
    if (
      props.storeLocator.isLocalised &&
      !props.storeLocator.collectionLoading &&
      !props.storeLocator.deliveryLoading
    ) {
      if (props.storeLocator.fetchAlternativeStore && props.attributes.collectable) {
        fetchAlternativeStore()
      } else {
        updateDataLayer()
      }
    }
  }, [props.storeLocator.collectionLoading, props.storeLocator.deliveryLoading])

  useEffect(() => {
    async function updateQuantity() {
      if (props.storeLocator.deliveryPostcode === '') return

      await props.localiseFulfilment({
        ...props.fulfilmentPayload,
        quantityChangeRequest: true,
        landingPageRequest: false
      })
    }
    if (isInitialMount.current !== 'pdp_initialStockAPIs') {
      updateIsInitialMount('pdp_quantityStockAPIs')
      updateQuantity()
    }
  }, [props.quantity])

  const getActiveGlobalSearchTerm = () => deliverySearchTerm || initialSearchTerm || ''

  const UpdateInitialSearchTerm = event => {
    event.preventDefault()
    setInitialSearchTerm(event.target.value)
  }

  const updateDeliverySearchTerm = event => {
    event.preventDefault()
    setDeliverySearchTerm(event.target.value)
  }

  const openStoreSelector = event => {
    event.preventDefault()
    const globalTerm = props.storeLocator.currentSearchTerm || initialSearchTerm || ''
    setStoreSelectorVisible(true)
    setStoreSelectorSearchTerm(globalTerm)

    /*
    * The condition below is does not seem to valid anymore, hence I've inferred what I believe to be the correct behavior. Need to check with analytics

    const isChangeStoreEvent = event.target.className.includes('change-store')
    if (isChangeStoreEvent) {
      pushEventAndTrack({
        eventInfo: {
          eventName: 'pdp_fulfillmentModulesInteraction',
          eventAction: 'PDP Fulfillment Modules Interaction'
        },
        attributes: {
          moduleType: 'collection',
          clickOrigin: 'collectionChangeStore'
        }
      })
    } else {
      pushEventAndTrack({
        eventInfo: {
          eventName: 'pdp_deliveryLocationCheck',
          eventAction: 'Check Location For Delivery',
          productID: props.attributes.partNumber
        }
      })
    } */

    pushEventAndTrack({
      eventInfo: {
        eventName: 'pdp_fulfillmentModulesInteraction',
        eventAction: 'PDP Fulfillment Modules Interaction'
      },
      attributes: {
        moduleType: 'collection',
        clickOrigin: 'collectionChangeStore'
      }
    })
  }

  const searchStock = event => {
    event.preventDefault()

    if (isUkPostcode(initialSearchTerm) || (props.attributes.deliverable && !props.attributes.collectable)) {
      const postcode = (getActiveGlobalSearchTerm() || '').trim()
      searchDelivery(props, postcode, isInitialMount)
    } else {
      openStoreSelector(event)
    }
    return undefined
  }

  // eslint-disable-next-line
  const closeStoreSelector = async (
    event,
    storeSelected = null,
    goToDelivery = false,
    productSelected = null,
    searchTerm
  ) => {
    setStoreSelectorVisible(false)

    // Select Store
    if (storeSelected) {
      const { id } = storeSelected
      const fulfilmentPayload = props.fulfilmentPayload || {}
      await props.localiseFulfilment({
        ...fulfilmentPayload,
        origin: `st:${id}`,
        fromStoreSelector: true,
        landingPageRequest: false,
        searchTerm,
        goToDelivery // not needed but added for eslint purposes
      })
    }

    updateIsInitialMount('pdp_SLNewStoreStockAPIs')

    // Add to Trolley
    if (productSelected) {
      props.trolleyButtonTracking('PDP:storeSelectorATT')()
    }
  }

  const onShowDeliveryForm = () => {
    setShowDeliveryForm(true)
    setDeliverySearchTerm('')

    pushEventAndTrack({
      eventInfo: {
        eventName: 'pdp_fulfillmentModulesInteraction',
        eventAction: 'PDP Fulfillment Modules Interaction'
      },
      attributes: {
        moduleType: 'delivery',
        clickOrigin: 'deliveryChangePostcode'
      }
    })
  }

  const { collectable, deliverable } = getRenderOptions(props)

  const showCheckBox = () => {
    if (!collectable && !deliverable) return true
    if (!props.storeLocator.isLocalised && (collectable || deliverable)) return true
    return false
  }

  const findButtonText = 'Check'
  const checkStoreBox = {
    initialSearchTerm,
    searchStock,
    UpdateInitialSearchTerm,
    props,
    findButtonText
  }

  const collectionBox = {
    openStoreSelector,
    chooseAlternativeStore,
    addToTrolley,
    updateIsInitialMount,
    props
  }

  const deliveryBox = {
    props,
    searchDelivery,
    addToTrolley,
    deliverySearchTerm,
    showDeliveryForm,
    onShowDeliveryForm,
    findButtonText,
    updateDeliverySearchTerm,
    updateIsInitialMount
  }

  const storeSelectorLightBox = {
    props,
    storeSelectorSearchTerm,
    closeStoreSelector
  }

  const { sdf } = props.attributes

  const isV2 = props.isV2

  return (
    <div data-test='fulfilment-box-container'>
      {props.storeLocator.storeNumber && (
        <div itemProp='availableAtOrFrom' itemScope itemType='https://schema.org/Place'>
          <meta itemProp='branchCode' content={props.storeLocator.storeNumber} />
        </div>
      )}

      {showCheckBox() && <CheckStoreBox {...checkStoreBox} />}

      {/* Control */}
      {!isV2 && (
        <>
          {collectable && <CollectionBox {...collectionBox} />}
          {deliverable && <DeliveryBox {...deliveryBox} />}
        </>
      )}

      {/* Variant v2.2 */}
      {isV2 &&
        !showCheckBox() &&
        (() => {
          const fulfilmentProps = {
            collectable,
            collectionMessage: props.storeLocator.collectionMessage,
            collectionMessageKey: props.storeLocator.collectionMessageKey,
            collectionIcon: props.storeLocator.collectionIcon,
            collectionStore: props.storeLocator.collectionStore,
            collectionAdditionalContent: <CollectionBox {...collectionBox} alternativeMessagingOnly />,
            deliverable,
            deliveryMessage: props.storeLocator.deliveryMessage,
            deliveryMessageKey: props.storeLocator.deliveryMessageKey,
            deliveryIcon: props.storeLocator.deliveryIcon,
            deliveryPostcode: props.storeLocator.deliveryPostcode,
            deliveryPostcodeForm: showDeliveryForm && <DeliveryBox {...deliveryBox} deliveryFormOnly />,
            deliveryAdditionalContent: <DeliveryBox {...deliveryBox} emailFormOnly />,
            supplierInfoCTA: sdf ? <DeliveryBox {...deliveryBox} supplierInfoCtaOnly /> : null,
            isLoading: props.storeLocator.collectionLoading || props.storeLocator.deliveryLoading,
            openStoreSelector,
            onShowDeliveryForm
          }
          return <FulfilmentBoxV2 {...fulfilmentProps} />
        })()}

      {storeSelectorVisible && <StoreSelectorLightBox {...storeSelectorLightBox} />}
    </div>
  )
}

FulfilmentBox.propTypes = {
  attributes: PropTypes.object,
  collectionMessage: PropTypes.string,
  collectionStore: PropTypes.string,
  deliveryPostcode: PropTypes.string,
  deliveryMessage: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  alternativeStore: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  quantity: PropTypes.number,
  deliveryUpdateTimestamp: PropTypes.number,
  chooseAlternativeStore: PropTypes.func,
  variants: PropTypes.object,
  storeLocator: PropTypes.shape({
    deliveryError: PropTypes.string,
    collectionError: PropTypes.string,
    stockCheckAvailable: PropTypes.bool,
    collectionLoading: PropTypes.bool,
    deliveryLoading: PropTypes.bool,
    regionalIsAvailable: PropTypes.bool,
    regionalMessage: PropTypes.string,
    collectionMessage: PropTypes.string,
    deliveryMessage: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    storeNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currentSearchTerm: PropTypes.string,
    deliveryPostcode: PropTypes.string,
    isLocalised: PropTypes.bool,
    fetchAlternativeStore: PropTypes.bool,
    cisCookieStatus: PropTypes.string,
    alternativeStore: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    deliveryUpdateTimestamp: PropTypes.number
  }).isRequired
}

export { searchDelivery, getRenderOptions, chooseAlternativeStore, addToTrolley }
export default FulfilmentBox
